import { TREE_NODE_HEIGHT } from "@/components/ui/tree/tree-node";
import { selectIsEntitySetToVisible } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-visibility-selectors";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  blue,
  FaroIconButton,
  FaroIconButtonSizes,
  FaroText,
  grey,
  NonVisibleIcon,
  TranslateVar,
  TruncatedFaroText,
  VisibleIcon,
  yellow,
} from "@faro-lotv/flat-ui";
import { CaptureTreeEntityRevision } from "@faro-lotv/service-wires";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
} from "@mui/material";
import { Stack } from "@mui/system";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { TreeApi } from "react-arborist";
import { ReactComponent as BadgeIcon } from "../../icons/badge.svg";
import {
  setSelectedEntityIds,
  setVisibilityForEntities,
} from "../../store/data-preparation-ui/data-preparation-ui-slice";
import { ActionTree } from "./action-tree";

const HIDDEN_ENTITY_OPACITY = 0.6;

/**
 * Props for the ActionGroupAccordion component
 */
export type ActionGroupAccordionProps = {
  /** Array of capture tree entity revisions that form a connected group */
  group: CaptureTreeEntityRevision[];
  /** Zero-based index of this group in the list of all groups */
  groupIndex: number;
  /** Currently selected items IDs */
  selectedActionPanelIds: string[];
  /** Map of parent entity IDs to their unconnected child entities */
  unconnectedEntityChildrenMap: Map<string, CaptureTreeEntityRevision[]>;
  /** Redux dispatch function for dispatching actions */
  dispatch: ReturnType<typeof useAppDispatch>;
  /** Selected entity IDs in the main view */
  selectedEntityIds: string[];
};

/** @returns an accordion for a single group of connected scans with issues */
export function ActionGroupAccordion({
  group,
  groupIndex,
  selectedActionPanelIds,
  unconnectedEntityChildrenMap,
  dispatch,
  selectedEntityIds,
}: ActionGroupAccordionProps): JSX.Element {
  const actionPanelTreeRef = useRef<TreeApi<CaptureTreeEntityRevision>>(null);

  // Filter selected IDs to only those present in this group
  const selectedIdsInThisGroup = useMemo(() => {
    const groupIds = new Set(group.map((entity) => entity.id));
    return selectedActionPanelIds.filter((id) => groupIds.has(id));
  }, [selectedActionPanelIds, group]);

  const [isGroupHovered, setIsGroupHovered] = useState(false);

  // Sync the selection state with the tree
  useEffect(() => {
    const tree = actionPanelTreeRef.current;
    if (!tree) return;

    tree.setSelection({
      ids: selectedIdsInThisGroup,
      anchor: selectedIdsInThisGroup.at(0) ?? null,
      mostRecent: selectedIdsInThisGroup.at(-1) ?? null,
    });
  }, [selectedIdsInThisGroup]);

  const numberOfScans = group.length;

  // Get all entity IDs in this group
  const groupEntityIds = useMemo(
    () => group.map((entity) => entity.id),
    [group],
  );

  // Calculate visibility state for the group
  const isGroupVisible = useAppSelector((state) =>
    groupEntityIds.some((entityId) =>
      selectIsEntitySetToVisible(entityId)(state),
    ),
  );

  const isGroupSelected = useMemo(
    () =>
      groupEntityIds.every((entityId) => selectedEntityIds.includes(entityId)),
    [groupEntityIds, selectedEntityIds],
  );

  // Handle selecting all scans in the group
  const handleSelectGroup = useCallback(
    (ev: React.MouseEvent): void => {
      // Prevent accordion collapse/expand
      ev.stopPropagation();

      if (isGroupSelected) {
        // Deselect all scans in the group
        const remainingSelection = selectedEntityIds.filter(
          (id) => !groupEntityIds.includes(id),
        );
        dispatch(setSelectedEntityIds(remainingSelection));
      } else {
        // Select all scans in the group
        dispatch(setSelectedEntityIds(groupEntityIds));
      }
    },
    [isGroupSelected, selectedEntityIds, groupEntityIds, dispatch],
  );

  // Handle visibility toggle
  const handleToggleVisibility = useCallback(
    (ev: React.MouseEvent): void => {
      ev.stopPropagation();

      // if the entity is visible, toggle should make the entity and affected descendants/ascendants non-visible
      if (isGroupVisible) {
        dispatch(
          setVisibilityForEntities({
            entityIds: groupEntityIds,
            isVisible: false,
          }),
        );
      }
      // if the entity is non-visible, toggle should make the entity and affected descendants/ascendants visible
      else {
        dispatch(
          setVisibilityForEntities({
            entityIds: groupEntityIds,
            isVisible: true,
          }),
        );
      }
    },
    [isGroupVisible, dispatch, groupEntityIds],
  );

  // If there's only one scan, render it directly without an accordion
  if (numberOfScans === 1) {
    return (
      <>
        {groupIndex !== 0 && (
          <Divider
            aria-label="registration-action-group-divider"
            sx={{ my: 0.25 }}
          />
        )}
        <ActionTree
          treeRef={actionPanelTreeRef}
          data={group}
          height={TREE_NODE_HEIGHT}
          selectedIdsInThisGroup={selectedIdsInThisGroup}
          unconnectedEntityChildrenMap={unconnectedEntityChildrenMap}
          dispatch={dispatch}
        />
      </>
    );
  }

  return (
    <>
      {groupIndex !== 0 && (
        <Divider
          aria-label="registration-action-group-divider"
          sx={{ my: 1 }}
        />
      )}
      <Accordion
        sx={{
          width: "100%",
          background: "transparent",
          boxShadow: "none",
          overflow: "hidden",
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {
            margin: 0,
          },
        }}
        defaultExpanded
      >
        <AccordionSummary
          component="div"
          expandIcon={<ExpandMoreIcon sx={{ fontSize: "1.125em" }} />}
          sx={{
            minHeight: "auto",
            paddingLeft: 0,
            paddingRight: 0.125,
            flexDirection: "row-reverse",
            backgroundColor: isGroupSelected ? blue[500] : "transparent",
            color: isGroupSelected ? grey[0] : "inherit",
            transition: "background-color 0.2s, color 0.2s",
            "&:hover": {
              backgroundColor: isGroupSelected
                ? blue[500]
                : "rgba(0, 0, 0, 0.1)",

              color: isGroupSelected ? grey[0] : grey[800],
            },
            "& .MuiAccordionSummary-expandIconWrapper": {
              marginRight: 1,
              marginLeft: 0,
              transform: "rotate(-90deg)",
              transition: "none",
              color: "inherit",
            },
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "rotate(0deg)",
            },
            "&.Mui-expanded": {
              minHeight: "auto",
              margin: 0,
            },
            "& .MuiAccordionSummary-content": {
              margin: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minWidth: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },
          }}
        >
          <Stack
            sx={{ width: "100%", justifyContent: "space-between", gap: 1 }}
            direction="row"
            onPointerEnter={() => setIsGroupHovered(true)}
            onPointerLeave={() => setIsGroupHovered(false)}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                // Don't wrap to next line on small sidebar, truncate text
                overflowX: "hidden",
              }}
            >
              <TruncatedFaroText
                variant="bodyM"
                onClick={handleSelectGroup}
                containerSx={{
                  cursor: "pointer",
                  color: "inherit",
                  userSelect: "none",
                  opacity: isGroupVisible ? 1 : HIDDEN_ENTITY_OPACITY,
                  paddingRight: 0.5,
                  minWidth: 0,
                }}
                sx={{
                  color: "inherit",
                }}
              >
                Connected captures
              </TruncatedFaroText>
              <FaroText
                variant="bodyM"
                lineHeight={1.75}
                onClick={handleSelectGroup}
                color="inherit"
                sx={{
                  cursor: "pointer",
                  flexShrink: 0,
                  userSelect: "none",
                  opacity: isGroupVisible ? 1 : HIDDEN_ENTITY_OPACITY,
                }}
              >
                (
                <TranslateVar name="numberOfScans">
                  {numberOfScans}
                </TranslateVar>
                )
              </FaroText>

              <Box
                sx={{
                  ml: 1,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <BadgeIcon
                  style={{
                    color: yellow[500],
                    width: 12,
                    height: 12,
                  }}
                />
              </Box>
            </Stack>
            <FaroIconButton
              size={FaroIconButtonSizes.xs}
              onClick={handleToggleVisibility}
              sx={{
                color: "inherit",
                opacity: isGroupVisible ? 1 : HIDDEN_ENTITY_OPACITY,
                "& >:first-of-type": { color: "inherit" },
              }}
            >
              <>
                {isGroupVisible && (isGroupHovered || isGroupSelected) && (
                  <VisibleIcon />
                )}
                {!isGroupVisible && <NonVisibleIcon />}
              </>
            </FaroIconButton>
          </Stack>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: 0,
            height: "auto",
            overflow: "hidden",
          }}
        >
          <ActionTree
            treeRef={actionPanelTreeRef}
            data={group}
            height={numberOfScans * TREE_NODE_HEIGHT}
            selectedIdsInThisGroup={selectedIdsInThisGroup}
            unconnectedEntityChildrenMap={unconnectedEntityChildrenMap}
            dispatch={dispatch}
          />
        </AccordionDetails>
      </Accordion>
    </>
  );
}
